/**
 * useTokenExpiry Hook
 *
 * Checks if the stored JWT token is expired on app load.
 * If expired, clears the auth state and redirects to login.
 *
 * @module hooks/useTokenExpiry
 */

import { useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setIsLoggedIn } from "../redux/slices/generalSlice";
import notify from "../components/common/notify";

/**
 * Parse JWT token payload without verifying signature
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
const parseJwt = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

/**
 * Check if token is expired or about to expire
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Buffer time before actual expiry (default: 60 seconds)
 * @returns {{ isExpired: boolean, expiresIn: number|null }} Token expiry status
 */
const checkTokenExpiry = (token, bufferSeconds = 60) => {
  if (!token) {
    return { isExpired: true, expiresIn: null };
  }

  const payload = parseJwt(token);
  if (!payload || !payload.exp) {
    return { isExpired: true, expiresIn: null };
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = payload.exp - now;
  const isExpired = expiresIn <= bufferSeconds;

  return { isExpired, expiresIn };
};

/**
 * Hook to check token expiry on app load and handle cleanup
 *
 * @param {Object} options - Hook options
 * @param {boolean} options.showNotification - Whether to show notification on expiry (default: true)
 * @param {Function} options.onExpired - Callback when token is expired
 * @returns {{ isTokenValid: boolean, checkNow: Function }}
 */
const useTokenExpiry = (options = {}) => {
  const { showNotification = true, onExpired } = options;
  const dispatch = useDispatch();

  /**
   * Clear all auth data from storage
   */
  const clearAuthData = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("auth_provider");
    localStorage.removeItem("isSignMessageShowed");
    dispatch(setIsLoggedIn(false));
  }, [dispatch]);

  /**
   * Check token and handle expiry
   * @returns {boolean} Whether token is valid
   */
  const checkNow = useCallback(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      return false;
    }

    const { isExpired, expiresIn } = checkTokenExpiry(token);

    if (isExpired) {
      clearAuthData();

      if (showNotification) {
        notify("Your session has expired. Please log in again.", "info");
      }

      if (onExpired) {
        onExpired();
      }

      return false;
    }

    // Log warning if token expires soon (within 30 minutes)
    if (expiresIn !== null && expiresIn < 1800) {
      console.log(`Token expires in ${Math.floor(expiresIn / 60)} minutes`);
    }

    return true;
  }, [clearAuthData, showNotification, onExpired]);

  // Check token on mount
  useEffect(() => {
    checkNow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set up periodic check (every 5 minutes)
  useEffect(() => {
    const intervalId = setInterval(() => {
      checkNow();
    }, 5 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, [checkNow]);

  return {
    checkNow,
    isTokenValid: checkNow(),
  };
};

export default useTokenExpiry;
export { parseJwt, checkTokenExpiry };
